import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DatasourceFileResponseIntermediate, FileDataRequestParams, IFileDataSourceApiService } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { IAttachmentFile, SalesforceFileReaderService } from './salesforce.filereader.service';

export type FileDataRequestParamsSalesforce = FileDataRequestParams & {
  downloadUrl: string;
  type: string; // e.g., 'PDF', 'WORD_X', 'WORD'
};

export interface IQueryResp {
  records: { Id: string }[];
  done: boolean;
  totalSize: number;
}

@Injectable({
  providedIn: 'root',
})
export class SalesforceFileDataSourceApiService implements IFileDataSourceApiService {
  private static readonly TR1_CONTACT_DOCUMENT_SOURCE_TYPE = 'TR1_CONTACT_DOCUMENT';
  private fileService: SalesforceFileReaderService;

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
  ) {
    this.fileService = new SalesforceFileReaderService();
  }

  postFile(file: File, fileParams: any): Observable<any> {
    // post to update a file in Bh, Put to add a new one
    return this.fileService.getFileContents(file).pipe(switchMap((attachmentFile) => this.putFile(attachmentFile, fileParams)));
  }

  checkFile(id: string, fileParams: any): Observable<boolean> {
    SalesforceFileDataSourceApiService.assertFileParams(fileParams);

    const maybeContactDocumentParams = fileParams?.parameters;
    if (maybeContactDocumentParams?.sourceType === SalesforceFileDataSourceApiService.TR1_CONTACT_DOCUMENT_SOURCE_TYPE) {
      // We already have a pre-signed URL; trust it and skip the extra ContentVersion lookup.
      return of(true);
    }

    const decoded = jwtDecode<any>(this.identityStorage.get()?.token as string);
    const base = `${decoded.instanceUrl}/services/data/${decoded.apiVersion}`;

    // 1) Get latest ContentVersion.Id for this ContentDocument
    const soql = `SELECT Id FROM ContentVersion
                WHERE ContentDocumentId = '${id}' AND IsLatest = true
                ORDER BY VersionNumber DESC
                LIMIT 1`;
    const getVersionUrl = `${decoded.instanceUrl}/services/data/v60.0/query?q=${encodeURIComponent(soql)}`;

    return this.http.get<IQueryResp>(getVersionUrl).pipe(
      map((r) => r.records?.[0]?.Id),
      // Fail fast if no version exists
      switchMap((verId) => {
        if (!verId) {
          return throwError(() => new Error('No ContentVersion found for ContentDocument'));
        }
        // 2) Fetch the bytes for that version
        const versionDataUrl = `${base}/sobjects/ContentVersion/${verId}/versionData`;
        const headers = new HttpHeaders({ Accept: 'application/octet-stream' });
        return this.http.get(versionDataUrl, {
          headers,
          responseType: 'blob' as 'json', // treat as Blob
          observe: 'response',
        });
      }),
      tap((resp) => {
        console.log('Fetched versionData, status:', resp.status);
      }),
      map(() => true),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
        return throwError(() => err);
      }),
    );
  }

  getFileData(fileDatasourcePath: string[], fileData?: FileDataRequestParamsSalesforce): Observable<DatasourceFileResponseIntermediate> {
    if (!fileData?.downloadUrl) {
      const contentDocumentId = fileDatasourcePath?.[3];
      if (!contentDocumentId || !fileData) {
        return throwError(() => new Error('No download URL provided for Salesforce file data request.'));
      }
      return this.resolveContentVersionDownloadUrl(contentDocumentId).pipe(
        switchMap((resolvedUrl) =>
          this.getFileData(fileDatasourcePath, { ...fileData, downloadUrl: resolvedUrl }),
        ),
      );
    }

    if (!fileData.type) {
      // We can fallback to check file extension but it's not reliable.
      // And we can fallback to use the octetstream data received, but it would require extra processing
      // from the file-preview-helper service to correctly handle it.
      // It's better to just error out here and ask the caller to provide the type.
      return throwError(() => new Error('No file type provided for Salesforce file data request.'));
    }

    const fileContentType = this.determineFileContentType(fileData.type);

    if (!this.isDownloadUrlContactDocument(fileData.downloadUrl)) {
      // Happy path, not a contact document
      const downloadFullUrl = this.buildApiUrl(fileData.downloadUrl);
      return this.http
        .get(downloadFullUrl, { responseType: 'blob', observe: 'response' })
        .pipe(map((response) => this.mapToBlobResponse(response, fileData, fileContentType)));
    }

    // Contact document - need to get the real file url
    const publicUrlRequest = this.buildContactDocumentApiUrl(fileData.downloadUrl);
    return this.http.get(publicUrlRequest, { responseType: 'text' }).pipe(
      switchMap((publicUrl) => {
        if (!publicUrl || typeof publicUrl !== 'string' || !publicUrl.startsWith('http')) {
          return throwError(() => new Error('No public URL returned from contact document API.'));
        }

        // Fix malformed URL with space after protocol (e.g., "https: //" -> "https://")
        const fixedUrl = publicUrl.replace(/^(https?):\s+\/\//, '$1://');
        if (fixedUrl !== publicUrl) {
          console.warn('Fixed malformed Salesforce URL:', { original: publicUrl, fixed: fixedUrl });
        }

        return this.http.get(fixedUrl, { responseType: 'blob', observe: 'response' });
      }),
      map((response) => this.mapToBlobResponse(response, fileData, fileContentType)),
    );
  }

  private mapToBlobResponse(
    response: HttpResponse<Blob>,
    fileData: FileDataRequestParamsSalesforce,
    fileContentType: string,
  ): DatasourceFileResponseIntermediate {
    if (!response.body) {
      throw new Error('No file data received from server');
    }
    return {
      contentType: fileContentType,
      fileBinaryData: new Blob([response.body], { type: fileContentType }),
      name: fileData.name,
    };
  }

  private isDownloadUrlContactDocument(downloadUrl: string): boolean {
    return downloadUrl.includes('/contact-documents/') && downloadUrl.endsWith('/public-url');
  }

  private determineFileContentType(fileType: string): string {
    switch (fileType.toLocaleLowerCase()) {
      case 'pdf':
        return 'application/pdf';
      case 'word_x':
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'word':
      case 'doc':
        return 'application/msword';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      default:
        // Fail explicitly - or use 'application/octetstream' and add support for unknown types in file-preview-loader service.
        throw new Error(`Unsupported file type: ${fileType}`);
    }
  }

  private buildApiUrl(path: string): string {
    const jwtPayload = jwtDecode<any>(this.identityStorage.get()?.token as string);
    return `${jwtPayload.instanceUrl}/services/data/${jwtPayload.apiVersion}${path}`;
  }

  private buildContactDocumentApiUrl(path: string): string {
    const jwtPayload = jwtDecode<any>(this.identityStorage.get()?.token as string);
    return `${jwtPayload.instanceUrl}${path}`;
  }

  private resolveContentVersionDownloadUrl(contentDocumentId: string): Observable<string> {
    const decoded = jwtDecode<any>(this.identityStorage.get()?.token as string);
    const escapedId = contentDocumentId.replace(/'/g, "\\'");
    const soql = `SELECT Id FROM ContentVersion WHERE ContentDocumentId = '${escapedId}' AND IsLatest = true ORDER BY VersionNumber DESC LIMIT 1`;
    const url = `${decoded.instanceUrl}/services/data/${decoded.apiVersion}/query?q=${encodeURIComponent(soql)}`;
    return this.http.get<IQueryResp>(url).pipe(
      map((r) => r.records?.[0]?.Id),
      switchMap((versionId) => {
        if (!versionId) {
          return throwError(() => new Error('No ContentVersion found for Salesforce file. Unable to preview.'));
        }
        return of(`/sobjects/ContentVersion/${versionId}/VersionData`);
      }),
    );
  }

  private putFile(attachmentFile: IAttachmentFile, fileParams: any): Observable<any> {
    SalesforceFileDataSourceApiService.assertFileParams(fileParams);

    const decoded = jwtDecode<any>(this.identityStorage.get()?.token as string);
    const base = `${decoded.instanceUrl}/services/data/${decoded.apiVersion}`;
    const parentId: string = fileParams?.entity?.id;

    // Normalize base64 (strip data URL prefix if present)
    const base64 = this.normalizeBase64(attachmentFile.fileContent);

    const body = {
      Title: attachmentFile.name,
      PathOnClient: attachmentFile.name,
      FirstPublishLocationId: parentId,
      VersionData: base64, // <- Base64 bytes
      // ContentType: attachmentFile.contentType, // optional
    };

    return this.http.post<{ id: string; success: boolean; errors: any[] }>(`${base}/sobjects/ContentVersion`, body).pipe(
      tap((resp) => console.log('Uploaded ContentVersion', resp)),
      map((resp) => ({
        id: resp.id,
        type: fileParams.fileType,
        name: attachmentFile.name,
      })),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
        return throwError(() => err);
      }),
    );
  }

  private static assertFileParams(fileParams?: any): void {
    if (!fileParams) {
      throw new Error('No file parameters supplied for Salesforce checkFile(..).');
    }
  }

  private normalizeBase64(s: string): string {
    if (!s) {
      return s;
    }
    const idx = s.indexOf('base64,');
    return idx >= 0 ? s.substring(idx + 7) : s;
  }
}
